import { motion } from 'framer-motion';
import { Check, AlertTriangle, RotateCcw } from 'lucide-react';

const STAGE_ORDER = ['requesting_export', 'sending', 'building'];
const TOTAL_STAGES = STAGE_ORDER.length;

// The app owns its dispatch vocabulary. Each `dispatch_progress` event carries a
// server-authored `stage_label`, but a sync fetches the design from Canva — it
// never "exports" it — so any stage whose emitted copy is off-message is
// overridden here, at the point of display, keyed by the stable `stage` field.
// Stages absent from this map fall through to the server label (which preserves
// demo/variant wording). The `requesting_export` stage KEY is the n8n↔client
// event contract and is intentionally left unchanged.
const STAGE_LABEL_OVERRIDES = {
  requesting_export: 'Fetching design from Canva',
};

function stageLabelFor(entry) {
  return STAGE_LABEL_OVERRIDES[entry.stage] ?? entry.stageLabel ?? '';
}

function stageIndexFor(stage) {
  // queued (optimistic pre-emit) = 0, requesting_export = 1, sending = 2,
  // building = 3, done = 3, failed = current
  if (stage === 'done') return TOTAL_STAGES;
  const idx = STAGE_ORDER.indexOf(stage);
  return idx === -1 ? 0 : idx + 1;
}

function StepDots({ activeIndex, isFailed }) {
  return (
    <div className="flex items-center gap-1.5" aria-label={`Stage ${activeIndex} of ${TOTAL_STAGES}`}>
      {Array.from({ length: TOTAL_STAGES }).map((_, i) => {
        const stepNum = i + 1;
        const reached = stepNum <= activeIndex;
        const failedHere = isFailed && stepNum === activeIndex;
        return (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              reached ? (failedHere ? 'bg-red-500 w-6' : 'bg-primary-400 w-6') : 'bg-white/30 w-3'
            }`}
          />
        );
      })}
    </div>
  );
}

function StationRow({ stationLabel, entry, onRetry }) {
  const isFailed = entry.stage === 'failed';
  const isDone = entry.stage === 'done';
  const activeIndex = stageIndexFor(entry.stage);

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 rounded-md bg-black/40">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold text-white truncate flex-1">{stationLabel}</span>
        {isDone && (
          <span className="flex items-center gap-1 text-green-300">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </span>
        )}
        {isFailed && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1 text-red-300 hover:text-red-200 text-[11px] font-semibold uppercase tracking-wide"
          >
            <RotateCcw className="w-3 h-3" strokeWidth={2.5} />
            Retry
          </button>
        )}
      </div>
      <StepDots activeIndex={activeIndex} isFailed={isFailed} />
      <div className="text-[11px] leading-tight">
        {isFailed ? (
          <span className="text-red-300 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="truncate">
              {entry.errorMessage || `Failed at ${entry.failedAtStage || 'unknown stage'}`}
            </span>
          </span>
        ) : (
          <span className="text-white/90">{stageLabelFor(entry)}</span>
        )}
        {!isFailed && entry.detail && (
          <div className="text-[10px] text-white/60 mt-0.5">{entry.detail}</div>
        )}
      </div>
    </div>
  );
}

/**
 * DispatchProgressOverlay
 *
 * Replaces the legacy "Sending..." spinner. Driven entirely by per-(design, agent)
 * state that AppContext maintains from inbound `dispatch_progress` Ably events.
 *
 * @param {object} progressByAgent - Map of agentId → progress entry (from state.dispatchProgress[designId])
 * @param {Array}  agents          - Linked agents list, used to resolve agent_id → human-readable description
 * @param {(agentId: string) => void} onRetry - Per-station retry callback (re-trigger that station's dispatch)
 */
export default function DispatchProgressOverlay({ progressByAgent, agents, onRetry }) {
  const agentIds = Object.keys(progressByAgent || {});
  if (agentIds.length === 0) return null;

  const labelFor = (agentId) => {
    const a = agents?.find(x => x.agent_id === agentId);
    return a?.description || a?.name || 'Media Station';
  };

  const single = agentIds.length === 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col p-3 gap-2 z-10 overflow-y-auto"
    >
      {single ? (
        <div className="flex flex-col justify-center h-full">
          <StationRow
            stationLabel={labelFor(agentIds[0])}
            entry={progressByAgent[agentIds[0]]}
            onRetry={() => onRetry?.(agentIds[0])}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2 my-auto">
          {agentIds.map(agentId => (
            <StationRow
              key={agentId}
              stationLabel={labelFor(agentId)}
              entry={progressByAgent[agentId]}
              onRetry={() => onRetry?.(agentId)}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
